import moment from 'moment';

/**
 * Calculate dynamic insights based on object type and data
 * @param {string} objectType - Type of object (deals, contacts, companies)
 * @param {object} data - Object data from API
 * @returns {array} Array of insight objects
 */
export const calculateInsights = (objectType, data) => {
  if (!data) return [];

  const insights = [];

  // Common insights for all object types
  insights.push({
    label: 'Created',
    value: data.createdAt ? moment(data.createdAt).format('MMM D, YYYY') : '-',
    icon: 'calendar',
    tooltip: data.createdAt ? `Created on ${moment(data.createdAt).format('LLLL')}` : null
  });

  // Object-specific insights
  switch (objectType) {
    case 'deals':
      insights.push(...getDealInsights(data));
      break;
    case 'contacts':
      insights.push(...getContactInsights(data));
      break;
    case 'companies':
      insights.push(...getCompanyInsights(data));
      break;
    default:
      break;
  }

  // Add total activities insight
  if (data.activities && Array.isArray(data.activities)) {
    const totalActivities = data.activities.length;
    const recentActivities = data.activities.filter(a => 
      moment().diff(moment(a.createdAt || a.date), 'days') <= 7
    ).length;
    
    insights.push({
      label: 'Total Activities',
      value: totalActivities.toString(),
      subValue: `${recentActivities} this week`,
      icon: 'activity',
      tooltip: `${totalActivities} total activities, ${recentActivities} in the last 7 days`,
      status: recentActivities > 5 ? 'success' : recentActivities > 0 ? 'warning' : 'danger'
    });
  }

  // Add last activity insight if available
  if (data.lastActivityDate || data.updatedAt) {
    const lastActivity = data.lastActivityDate || data.updatedAt;
    const daysAgo = moment().diff(moment(lastActivity), 'days');
    insights.push({
      label: 'Last Activity',
      value: daysAgo === 0 ? 'Today' : daysAgo === 1 ? 'Yesterday' : `${daysAgo} days ago`,
      icon: 'clock',
      tooltip: moment(lastActivity).format('LLLL'),
      status: daysAgo > 30 ? 'warning' : 'success'
    });
  }

  // Add AI insights if available
  const aiInsights = getAIInsights(objectType, data);
  if (aiInsights.length > 0) {
    insights.push(...aiInsights);
  }

  return insights;
};

/**
 * Get deal-specific insights
 */
const getDealInsights = (deal) => {
  const insights = [];

  // Deal value
  if (deal.amount !== undefined) {
    insights.push({
      label: 'Deal Value',
      value: formatCurrency(deal.amount),
      icon: 'dollar',
      status: deal.amount > 10000 ? 'success' : 'default'
    });
  }

  // Deal stage
  if (deal.stage && deal.pipelineId) {
    const currentStage = deal.pipelineId.stages?.find(s => s.id === deal.stage);
    const stageIndex = deal.pipelineId.stages?.findIndex(s => s.id === deal.stage) || 0;
    const totalStages = deal.pipelineId.stages?.length || 1;
    const progress = ((stageIndex + 1) / totalStages) * 100;

    insights.push({
      label: 'Stage',
      value: currentStage?.name || deal.stage,
      subValue: `${Math.round(progress)}% complete`,
      icon: 'progress',
      progress: progress,
      tooltip: `Pipeline: ${deal.pipelineId.name}`
    });

    // Time in current stage
    if (deal.stageChangedAt || deal.createdAt) {
      const stageEnteredDate = deal.stageChangedAt || deal.createdAt;
      const daysInStage = moment().diff(moment(stageEnteredDate), 'days');
      insights.push({
        label: 'Time in Stage',
        value: `${daysInStage} days`,
        icon: 'clock',
        tooltip: `Entered ${currentStage?.name || 'this stage'} on ${moment(stageEnteredDate).format('MMM D, YYYY')}`,
        status: daysInStage > 30 ? 'warning' : 'default'
      });
    }
  }

  // Expected close date
  if (deal.closeDate) {
    const daysToClose = moment(deal.closeDate).diff(moment(), 'days');
    insights.push({
      label: 'Expected Close',
      value: moment(deal.closeDate).format('MMM D, YYYY'),
      subValue: daysToClose > 0 ? `In ${daysToClose} days` : `${Math.abs(daysToClose)} days overdue`,
      icon: 'target',
      status: daysToClose < 0 ? 'danger' : daysToClose < 7 ? 'warning' : 'success'
    });
  }

  // Deal age
  if (deal.createdAt) {
    const ageInDays = moment().diff(moment(deal.createdAt), 'days');
    insights.push({
      label: 'Deal Age',
      value: `${ageInDays} days`,
      icon: 'clock',
      status: ageInDays > 90 ? 'warning' : 'default'
    });
  }

  // Win probability if available
  if (deal.probability !== undefined) {
    insights.push({
      label: 'Win Probability',
      value: `${deal.probability}%`,
      icon: 'trending',
      progress: deal.probability,
      status: deal.probability >= 70 ? 'success' : deal.probability >= 40 ? 'warning' : 'danger'
    });
  }

  // Associated contacts count
  if (deal.contacts && Array.isArray(deal.contacts)) {
    insights.push({
      label: 'Contacts',
      value: deal.contacts.length.toString(),
      subValue: deal.contacts.length === 1 ? '1 person' : `${deal.contacts.length} people`,
      icon: 'users',
      tooltip: 'Associated contacts'
    });
  }

  // Deal owner
  if (deal.owner) {
    insights.push({
      label: 'Owner',
      value: deal.owner.name || deal.owner.email || 'Unassigned',
      icon: 'user-check',
      tooltip: deal.owner.email || null
    });
  }

  // Deal priority
  if (deal.priority) {
    insights.push({
      label: 'Priority',
      value: deal.priority.charAt(0).toUpperCase() + deal.priority.slice(1),
      icon: 'target',
      status: deal.priority === 'high' ? 'danger' : deal.priority === 'medium' ? 'warning' : 'default'
    });
  }

  return insights;
};

/**
 * Get contact-specific insights
 */
const getContactInsights = (contact) => {
  const insights = [];

  // Contact engagement score (if available)
  const engagementScore = calculateEngagementScore(contact);
  if (engagementScore !== null) {
    insights.push({
      label: 'Engagement',
      value: getEngagementLabel(engagementScore),
      subValue: `${engagementScore}% active`,
      icon: 'user-check',
      progress: engagementScore,
      status: engagementScore >= 70 ? 'success' : engagementScore >= 40 ? 'warning' : 'danger'
    });
  }

  // Associated deals count
  if (contact.deals && contact.deals.length > 0) {
    const totalDealValue = contact.deals.reduce((sum, deal) => sum + (deal.amount || 0), 0);
    insights.push({
      label: 'Associated Deals',
      value: contact.deals.length.toString(),
      subValue: totalDealValue > 0 ? formatCurrency(totalDealValue) : null,
      icon: 'briefcase',
      tooltip: `Total value across ${contact.deals.length} deal(s)`
    });
  }

  // Communication stats
  const commStats = getContactCommunicationStats(contact);
  if (commStats) {
    insights.push({
      label: 'Communications',
      value: commStats.total.toString(),
      subValue: `${commStats.emails} emails, ${commStats.calls} calls`,
      icon: 'message-circle',
      tooltip: 'Total interactions'
    });
  }

  // Lead source
  if (contact.leadSource) {
    insights.push({
      label: 'Lead Source',
      value: contact.leadSource,
      icon: 'source'
    });
  }

  // Contact score
  if (contact.score !== undefined) {
    insights.push({
      label: 'Lead Score',
      value: contact.score.toString(),
      icon: 'trending-up',
      progress: Math.min(100, contact.score),
      status: contact.score >= 80 ? 'success' : contact.score >= 50 ? 'warning' : 'default'
    });
  }

  // Lifecycle stage
  if (contact.lifecycleStage) {
    insights.push({
      label: 'Lifecycle Stage',
      value: contact.lifecycleStage.charAt(0).toUpperCase() + contact.lifecycleStage.slice(1),
      icon: 'progress',
      tooltip: 'Current stage in customer journey'
    });
  }

  // Contact owner
  if (contact.owner) {
    insights.push({
      label: 'Owner',
      value: contact.owner.name || contact.owner.email || 'Unassigned',
      icon: 'user-check'
    });
  }

  // Email status
  if (contact.emailStatus) {
    insights.push({
      label: 'Email Status',
      value: contact.emailStatus,
      icon: 'message-circle',
      status: contact.emailStatus === 'subscribed' ? 'success' : 
              contact.emailStatus === 'unsubscribed' ? 'danger' : 'warning'
    });
  }

  return insights;
};

/**
 * Get company-specific insights
 */
const getCompanyInsights = (company) => {
  const insights = [];

  // Company size
  if (company.numberOfEmployees) {
    insights.push({
      label: 'Company Size',
      value: formatEmployeeCount(company.numberOfEmployees),
      icon: 'users',
      status: company.numberOfEmployees > 100 ? 'success' : 'default'
    });
  }

  // Annual revenue
  if (company.annualRevenue) {
    insights.push({
      label: 'Annual Revenue',
      value: formatCurrency(company.annualRevenue),
      icon: 'trending-up',
      status: company.annualRevenue > 1000000 ? 'success' : 'default'
    });
  }

  // Associated contacts
  if (company.contacts && company.contacts.length > 0) {
    insights.push({
      label: 'Contacts',
      value: company.contacts.length.toString(),
      subValue: `${company.contacts.filter(c => c.isPrimary).length} primary`,
      icon: 'users',
      tooltip: 'Associated contacts'
    });
  }

  // Open deals value
  if (company.deals && company.deals.length > 0) {
    const openDeals = company.deals.filter(d => d.status !== 'won' && d.status !== 'lost');
    const openDealsValue = openDeals.reduce((sum, deal) => sum + (deal.amount || 0), 0);
    
    insights.push({
      label: 'Open Opportunities',
      value: openDeals.length.toString(),
      subValue: openDealsValue > 0 ? formatCurrency(openDealsValue) : null,
      icon: 'dollar-sign',
      status: openDeals.length > 0 ? 'success' : 'default'
    });
  }

  // Industry
  if (company.industry) {
    insights.push({
      label: 'Industry',
      value: company.industry,
      icon: 'building'
    });
  }

  // Company type
  if (company.type) {
    insights.push({
      label: 'Company Type',
      value: company.type,
      icon: 'briefcase',
      tooltip: 'Business classification'
    });
  }

  // Location
  if (company.city || company.country) {
    const location = [company.city, company.state, company.country].filter(Boolean).join(', ');
    if (location) {
      insights.push({
        label: 'Location',
        value: location,
        icon: 'building',
        tooltip: 'Company headquarters'
      });
    }
  }

  // Lifetime value
  if (company.lifetimeValue) {
    insights.push({
      label: 'Lifetime Value',
      value: formatCurrency(company.lifetimeValue),
      icon: 'dollar-sign',
      status: company.lifetimeValue > 100000 ? 'success' : 'default'
    });
  }

  // Company owner
  if (company.owner) {
    insights.push({
      label: 'Account Manager',
      value: company.owner.name || company.owner.email || 'Unassigned',
      icon: 'user-check'
    });
  }

  return insights;
};

/**
 * Helper functions
 */
const formatCurrency = (amount) => {
  if (amount == null || amount === undefined || isNaN(amount)) {
    return '$0';
  }
  if (amount >= 1000000) {
    return `$${(amount / 1000000).toFixed(1)}M`;
  } else if (amount >= 1000) {
    return `$${(amount / 1000).toFixed(1)}K`;
  }
  return `$${amount.toLocaleString()}`;
};

const formatEmployeeCount = (count) => {
  if (count >= 1000) {
    return `${(count / 1000).toFixed(1)}K+`;
  }
  return count.toString();
};

const calculateEngagementScore = (contact) => {
  // Simple engagement calculation based on activities
  if (!contact.activities || contact.activities.length === 0) return 0;
  
  const recentActivities = contact.activities.filter(a => 
    moment().diff(moment(a.createdAt), 'days') <= 30
  );
  
  const score = Math.min(100, (recentActivities.length / 10) * 100);
  return Math.round(score);
};

const getEngagementLabel = (score) => {
  if (score >= 80) return 'Highly Engaged';
  if (score >= 50) return 'Engaged';
  if (score >= 20) return 'Low Engagement';
  return 'Inactive';
};

const getContactCommunicationStats = (contact) => {
  if (!contact.activities) return null;
  
  const stats = {
    total: contact.activities.length,
    emails: contact.activities.filter(a => a.type === 'email').length,
    calls: contact.activities.filter(a => a.type === 'call').length,
    meetings: contact.activities.filter(a => a.type === 'meeting').length
  };
  
  return stats;
};

/**
 * Generate AI-powered insights based on patterns and data analysis
 */
const getAIInsights = (objectType, data) => {
  const insights = [];

  if (objectType === 'deals') {
    // Deal velocity insight
    if (data.createdAt && data.stage && data.pipelineId) {
      const ageInDays = moment().diff(moment(data.createdAt), 'days');
      const stageProgress = data.pipelineId.stages?.findIndex(s => s.id === data.stage) || 0;
      const totalStages = data.pipelineId.stages?.length || 1;
      const avgDaysPerStage = ageInDays / (stageProgress + 1);
      
      if (avgDaysPerStage < 7) {
        insights.push({
          label: 'AI Insight',
          value: 'Fast Moving Deal',
          subValue: `${Math.round(avgDaysPerStage)} days per stage avg`,
          icon: 'trending-up',
          status: 'success',
          tooltip: 'This deal is progressing faster than average'
        });
      } else if (avgDaysPerStage > 30) {
        insights.push({
          label: 'AI Insight',
          value: 'Slow Progress',
          subValue: 'Consider follow-up actions',
          icon: 'activity',
          status: 'warning',
          tooltip: 'This deal is moving slower than typical deals'
        });
      }
    }

    // Deal risk assessment
    if (data.lastActivityDate) {
      const daysSinceActivity = moment().diff(moment(data.lastActivityDate), 'days');
      const closeDate = data.closeDate ? moment(data.closeDate) : null;
      const daysToClose = closeDate ? closeDate.diff(moment(), 'days') : null;
      
      if (daysSinceActivity > 14 && daysToClose && daysToClose < 30) {
        insights.push({
          label: 'AI Risk Alert',
          value: 'At Risk',
          subValue: 'No recent activity',
          icon: 'activity',
          status: 'danger',
          tooltip: 'Deal closing soon but no recent engagement'
        });
      }
    }

    // Next Best Action recommendation
    const nextAction = getNextBestAction(data);
    if (nextAction) {
      insights.push({
        label: 'AI Next Best Action',
        value: nextAction.action,
        subValue: nextAction.reason,
        icon: 'target',
        status: nextAction.urgency,
        tooltip: nextAction.details
      });
    }

    // Deal triggers and automation suggestions
    const triggers = getDealTriggers(data);
    if (triggers && triggers.length > 0) {
      insights.push({
        label: 'AI Triggers Active',
        value: `${triggers.length} trigger${triggers.length > 1 ? 's' : ''}`,
        subValue: triggers[0].trigger,
        icon: 'alert',
        status: 'warning',
        tooltip: `Active triggers: ${triggers.map(t => t.trigger).join(', ')}`
      });
    }

    // Workflow suggestions
    const suggestions = getWorkflowSuggestions(data);
    if (suggestions) {
      insights.push({
        label: 'AI Automation Tip',
        value: suggestions.title,
        subValue: suggestions.benefit,
        icon: 'activity',
        tooltip: suggestions.description
      });
    }
  }

  if (objectType === 'contacts') {
    // Engagement pattern insight
    if (data.activities && data.activities.length > 5) {
      const activityDates = data.activities
        .map(a => moment(a.createdAt || a.date))
        .sort((a, b) => b.diff(a));
      
      const gaps = [];
      for (let i = 0; i < activityDates.length - 1; i++) {
        gaps.push(activityDates[i].diff(activityDates[i + 1], 'days'));
      }
      
      const avgGap = gaps.reduce((a, b) => a + b, 0) / gaps.length;
      
      if (avgGap < 7) {
        insights.push({
          label: 'AI Insight',
          value: 'Highly Engaged',
          subValue: 'Frequent interactions',
          icon: 'trending-up',
          status: 'success',
          tooltip: `Average ${Math.round(avgGap)} days between activities`
        });
      }
    }

    // Best contact time (if call activities exist)
    const calls = data.activities?.filter(a => a.type === 'call' && a.outcome === 'connected') || [];
    if (calls.length >= 3) {
      const callHours = calls.map(c => moment(c.createdAt || c.date).hour());
      const avgHour = Math.round(callHours.reduce((a, b) => a + b, 0) / callHours.length);
      
      insights.push({
        label: 'AI Suggestion',
        value: `Best Call Time: ${avgHour > 12 ? avgHour - 12 : avgHour}${avgHour >= 12 ? 'PM' : 'AM'}`,
        icon: 'clock',
        tooltip: 'Based on successful call history'
      });
    }
  }

  if (objectType === 'companies') {
    // Growth potential
    if (data.numberOfEmployees && data.annualRevenue) {
      const revenuePerEmployee = data.annualRevenue / data.numberOfEmployees;
      
      if (revenuePerEmployee > 200000) {
        insights.push({
          label: 'AI Analysis',
          value: 'High Efficiency',
          subValue: `${formatCurrency(revenuePerEmployee)}/employee`,
          icon: 'trending-up',
          status: 'success',
          tooltip: 'Strong revenue per employee ratio'
        });
      }
    }

    // Deal momentum
    if (data.deals && data.deals.length > 0) {
      const recentDeals = data.deals.filter(d => 
        moment().diff(moment(d.createdAt), 'days') <= 90
      );
      
      if (recentDeals.length >= 2) {
        insights.push({
          label: 'AI Insight',
          value: 'Active Account',
          subValue: `${recentDeals.length} recent opportunities`,
          icon: 'activity',
          status: 'success',
          tooltip: 'Multiple deals in progress'
        });
      }
    }
  }

  return insights;
};

/**
 * Determine the next best action for a deal based on its current state
 */
const getNextBestAction = (deal) => {
  const daysSinceActivity = deal.lastActivityDate 
    ? moment().diff(moment(deal.lastActivityDate), 'days') 
    : moment().diff(moment(deal.createdAt), 'days');
  
  const closeDate = deal.closeDate ? moment(deal.closeDate) : null;
  const daysToClose = closeDate ? closeDate.diff(moment(), 'days') : null;
  
  // Get current stage info
  const currentStage = deal.pipelineId?.stages?.find(s => s.id === deal.stage);
  const stageIndex = deal.pipelineId?.stages?.findIndex(s => s.id === deal.stage) || 0;
  const totalStages = deal.pipelineId?.stages?.length || 1;
  const isEarlyStage = stageIndex < totalStages / 3;
  const isMidStage = stageIndex >= totalStages / 3 && stageIndex < (2 * totalStages) / 3;
  const isLateStage = stageIndex >= (2 * totalStages) / 3;

  // Analyze recent activities
  const recentActivities = deal.activities?.filter(a => 
    moment().diff(moment(a.createdAt || a.date), 'days') <= 30
  ) || [];
  
  const hasRecentEmail = recentActivities.some(a => a.type === 'email');
  const hasRecentCall = recentActivities.some(a => a.type === 'call');
  const hasRecentMeeting = recentActivities.some(a => a.type === 'meeting');

  // Decision logic for next best action
  
  // Critical: Deal closing very soon
  if (daysToClose && daysToClose <= 7 && daysToClose >= 0) {
    if (daysSinceActivity > 3) {
      return {
        action: 'Urgent Follow-up Call',
        reason: `Closing in ${daysToClose} days`,
        details: 'Deal is closing very soon. Make an immediate call to ensure everything is on track for closing.',
        urgency: 'danger'
      };
    }
    if (!hasRecentMeeting && isLateStage) {
      return {
        action: 'Schedule Final Meeting',
        reason: 'Pre-close alignment needed',
        details: 'Schedule a final meeting to review terms and ensure smooth closing.',
        urgency: 'warning'
      };
    }
  }

  // Overdue deals
  if (daysToClose && daysToClose < 0) {
    return {
      action: 'Renegotiate Timeline',
      reason: `${Math.abs(daysToClose)} days overdue`,
      details: 'Deal is past close date. Call to understand blockers and set new realistic timeline.',
      urgency: 'danger'
    };
  }

  // Stalled deals (no activity for extended period)
  if (daysSinceActivity > 21) {
    return {
      action: 'Re-engagement Campaign',
      reason: `No activity for ${daysSinceActivity} days`,
      details: 'Deal has gone cold. Start with a friendly check-in email, then follow up with a call.',
      urgency: 'warning'
    };
  }

  // Early stage recommendations
  if (isEarlyStage) {
    if (!hasRecentMeeting && deal.amount > 10000) {
      return {
        action: 'Schedule Discovery Call',
        reason: 'Build stronger relationship',
        details: 'High-value opportunity needs deeper discovery. Schedule a call to understand needs better.',
        urgency: 'default'
      };
    }
    if (daysSinceActivity > 7) {
      return {
        action: 'Send Educational Content',
        reason: 'Nurture early interest',
        details: 'Share relevant case studies or whitepapers to keep prospect engaged.',
        urgency: 'default'
      };
    }
  }

  // Mid stage recommendations
  if (isMidStage) {
    if (!hasRecentMeeting && daysSinceActivity > 7) {
      return {
        action: 'Demo or Presentation',
        reason: 'Advance the conversation',
        details: 'Schedule a product demo or solution presentation to move deal forward.',
        urgency: 'warning'
      };
    }
    if (deal.contacts?.length === 1) {
      return {
        action: 'Identify Decision Makers',
        reason: 'Single-threaded risk',
        details: 'Only one contact on the deal. Identify and engage additional stakeholders.',
        urgency: 'default'
      };
    }
  }

  // Late stage recommendations
  if (isLateStage) {
    if (!deal.amount || deal.amount === 0) {
      return {
        action: 'Confirm Pricing',
        reason: 'No deal value set',
        details: 'Deal is in late stage but missing value. Confirm and update pricing details.',
        urgency: 'warning'
      };
    }
    if (!hasRecentCall && daysSinceActivity > 5) {
      return {
        action: 'Closing Call',
        reason: 'Push for decision',
        details: 'Schedule a call to address any final concerns and push for a decision.',
        urgency: 'warning'
      };
    }
    if (!closeDate) {
      return {
        action: 'Set Close Date',
        reason: 'Missing timeline',
        details: 'Deal is in late stage but no close date set. Work with prospect to establish timeline.',
        urgency: 'default'
      };
    }
  }

  // Default recommendations based on patterns
  if (daysSinceActivity > 14) {
    return {
      action: 'Check-in Call',
      reason: 'Maintain momentum',
      details: 'Regular touchpoint needed to keep deal moving forward.',
      urgency: 'default'
    };
  }

  if (recentActivities.length > 5 && !hasRecentMeeting) {
    return {
      action: 'Face-to-Face Meeting',
      reason: 'High engagement opportunity',
      details: 'Prospect is highly engaged. Capitalize with an in-person or video meeting.',
      urgency: 'success'
    };
  }

  // Deal-specific recommendations based on properties
  if (deal.priority === 'high' && daysSinceActivity > 3) {
    return {
      action: 'Priority Follow-up',
      reason: 'High priority deal',
      details: 'This is marked as high priority. Ensure frequent touchpoints to maintain momentum.',
      urgency: 'warning'
    };
  }

  if (deal.probability && deal.probability >= 80 && !closeDate) {
    return {
      action: 'Confirm Timeline',
      reason: 'High probability, no date',
      details: 'Deal has high win probability but no close date. Work to establish firm timeline.',
      urgency: 'warning'
    };
  }

  // No specific action needed
  if (daysSinceActivity <= 3 && hasRecentMeeting) {
    return {
      action: 'On Track',
      reason: 'Recent engagement',
      details: 'Deal is progressing well with recent activity. Continue current approach.',
      urgency: 'success'
    };
  }

  // Default action
  return {
    action: 'Follow-up Email',
    reason: 'Maintain engagement',
    details: 'Send a value-add email to keep the conversation moving forward.',
    urgency: 'default'
  };
};

/**
 * Identify active triggers that should prompt immediate action
 */
const getDealTriggers = (deal) => {
  const triggers = [];
  const now = moment();
  
  // Stage-specific triggers
  const currentStage = deal.pipelineId?.stages?.find(s => s.id === deal.stage);
  const stageIndex = deal.pipelineId?.stages?.findIndex(s => s.id === deal.stage) || 0;
  const totalStages = deal.pipelineId?.stages?.length || 1;
  
  // Time in stage trigger
  if (deal.stageChangedAt || deal.createdAt) {
    const stageEnteredDate = deal.stageChangedAt || deal.createdAt;
    const daysInStage = now.diff(moment(stageEnteredDate), 'days');
    
    if (daysInStage > 30) {
      triggers.push({
        trigger: 'Stuck in Stage',
        action: 'Review deal blockers',
        automation: 'Send stage progression email sequence'
      });
    }
  }
  
  // No contact trigger
  if ((!deal.contacts || deal.contacts.length === 0) && deal.amount > 5000) {
    triggers.push({
      trigger: 'No Contact Associated',
      action: 'Add primary contact',
      automation: 'Alert deal owner to add contacts'
    });
  }
  
  // Missing critical information triggers
  if (!deal.amount && stageIndex > totalStages / 2) {
    triggers.push({
      trigger: 'Missing Deal Value',
      action: 'Update deal amount',
      automation: 'Send pricing discussion template'
    });
  }
  
  if (!deal.closeDate && stageIndex > totalStages / 2) {
    triggers.push({
      trigger: 'No Close Date',
      action: 'Set expected close date',
      automation: 'Schedule timeline discussion call'
    });
  }
  
  // Activity pattern triggers
  const recentActivities = deal.activities?.filter(a => 
    now.diff(moment(a.createdAt || a.date), 'days') <= 30
  ) || [];
  
  const emailCount = recentActivities.filter(a => a.type === 'email').length;
  const callCount = recentActivities.filter(a => a.type === 'call').length;
  
  // Email-only communication trigger
  if (emailCount > 5 && callCount === 0 && deal.amount > 10000) {
    triggers.push({
      trigger: 'Email-Only Communication',
      action: 'Schedule a call',
      automation: 'Send calendar invite for discovery call'
    });
  }
  
  // Competitor mentioned trigger
  if (deal.notes?.toLowerCase().includes('competitor') || 
      deal.activities?.some(a => a.notes?.toLowerCase().includes('competitor'))) {
    triggers.push({
      trigger: 'Competitor Mentioned',
      action: 'Address competitive concerns',
      automation: 'Send competitive battlecard to owner'
    });
  }
  
  // Budget mentioned trigger
  if (deal.notes?.toLowerCase().includes('budget') || 
      deal.activities?.some(a => a.notes?.toLowerCase().includes('budget'))) {
    triggers.push({
      trigger: 'Budget Discussion',
      action: 'Clarify budget and ROI',
      automation: 'Send ROI calculator template'
    });
  }
  
  // Multiple stakeholders trigger
  if (deal.contacts?.length > 3) {
    triggers.push({
      trigger: 'Multiple Stakeholders',
      action: 'Map decision process',
      automation: 'Send stakeholder alignment template'
    });
  }
  
  // High value trigger
  if (deal.amount > 50000 && !deal.activities?.some(a => a.type === 'meeting')) {
    triggers.push({
      trigger: 'High Value - No Meeting',
      action: 'Schedule executive meeting',
      automation: 'Loop in executive sponsor'
    });
  }
  
  // Renewal trigger
  if (deal.type === 'renewal' || deal.name?.toLowerCase().includes('renewal')) {
    const daysToClose = deal.closeDate ? moment(deal.closeDate).diff(now, 'days') : null;
    if (daysToClose && daysToClose < 60) {
      triggers.push({
        trigger: 'Renewal Approaching',
        action: 'Start renewal discussions',
        automation: 'Launch renewal campaign sequence'
      });
    }
  }
  
  // Engagement drop trigger
  if (recentActivities.length > 0) {
    const lastWeekActivities = recentActivities.filter(a => 
      now.diff(moment(a.createdAt || a.date), 'days') <= 7
    );
    const previousWeekActivities = recentActivities.filter(a => 
      now.diff(moment(a.createdAt || a.date), 'days') > 7 && 
      now.diff(moment(a.createdAt || a.date), 'days') <= 14
    );
    
    if (previousWeekActivities.length > 3 && lastWeekActivities.length === 0) {
      triggers.push({
        trigger: 'Engagement Dropped',
        action: 'Re-engage immediately',
        automation: 'Send re-engagement sequence'
      });
    }
  }
  
  return triggers;
};

/**
 * Suggest workflow automations based on deal patterns
 */
const getWorkflowSuggestions = (deal) => {
  const daysSinceCreated = moment().diff(moment(deal.createdAt), 'days');
  const currentStageIndex = deal.pipelineId?.stages?.findIndex(s => s.id === deal.stage) || 0;
  const totalStages = deal.pipelineId?.stages?.length || 1;
  const stageProgress = ((currentStageIndex + 1) / totalStages) * 100;
  
  // Early stage suggestions
  if (stageProgress < 30) {
    if (!deal.activities?.some(a => a.type === 'email' && a.subject?.includes('welcome'))) {
      return {
        title: 'Welcome Sequence',
        benefit: 'Increase engagement by 40%',
        description: 'Set up automated welcome email series for new opportunities'
      };
    }
    
    if (daysSinceCreated > 3 && !deal.activities?.some(a => a.type === 'call')) {
      return {
        title: 'Auto-Schedule Discovery',
        benefit: 'Save 30 min per deal',
        description: 'Automatically send calendar link after 3 days without a call'
      };
    }
  }
  
  // Mid stage suggestions
  if (stageProgress >= 30 && stageProgress < 70) {
    if (!deal.activities?.some(a => a.type === 'meeting' && a.title?.includes('demo'))) {
      return {
        title: 'Demo Automation',
        benefit: 'Reduce time to demo by 5 days',
        description: 'Auto-send demo scheduling link when deal reaches evaluation stage'
      };
    }
    
    if (deal.contacts?.length === 1) {
      return {
        title: 'Stakeholder Discovery',
        benefit: 'Increase win rate by 25%',
        description: 'Automated email to identify additional decision makers'
      };
    }
  }
  
  // Late stage suggestions
  if (stageProgress >= 70) {
    if (!deal.activities?.some(a => a.title?.includes('proposal') || a.subject?.includes('proposal'))) {
      return {
        title: 'Proposal Automation',
        benefit: 'Close deals 3 days faster',
        description: 'Auto-generate and send proposals when deal reaches negotiation'
      };
    }
    
    const daysToClose = deal.closeDate ? moment(deal.closeDate).diff(moment(), 'days') : null;
    if (daysToClose && daysToClose < 14) {
      return {
        title: 'Close Acceleration',
        benefit: 'Increase close rate by 20%',
        description: 'Daily automated touchpoints for deals closing within 2 weeks'
      };
    }
  }
  
  // Pattern-based suggestions
  if (deal.activities?.filter(a => a.type === 'email').length > 10) {
    return {
      title: 'Email Fatigue Prevention',
      benefit: 'Improve response rates',
      description: 'Mix communication channels - automate SMS and calling campaigns'
    };
  }
  
  if (deal.amount > 25000 && !deal.activities?.some(a => a.participants?.length > 2)) {
    return {
      title: 'Multi-threading Campaign',
      benefit: 'Reduce single-thread risk',
      description: 'Automated outreach to engage multiple stakeholders'
    };
  }
  
  // Time-based suggestions
  const daysSinceActivity = deal.lastActivityDate 
    ? moment().diff(moment(deal.lastActivityDate), 'days') 
    : daysSinceCreated;
    
  if (daysSinceActivity > 7 && daysSinceActivity < 14) {
    return {
      title: 'Smart Follow-up',
      benefit: 'Prevent deal stagnation',
      description: 'Set up 7-day activity monitoring with automated follow-ups'
    };
  }
  
  if (deal.probability && deal.probability > 70) {
    return {
      title: 'Win Probability Triggers',
      benefit: 'Focus on hot deals',
      description: 'Auto-escalate high probability deals to senior reps'
    };
  }
  
  // Default suggestion
  return {
    title: 'Activity Scoring',
    benefit: 'Prioritize effectively',
    description: 'Set up automated deal scoring based on engagement patterns'
  };
};

// Export functions for use in other components
export { getDealTriggers, getWorkflowSuggestions };